// fast-json-patch ships as CommonJS with no named ESM exports, so under Node's
// ESM resolver only the default (the module.exports object) is available. Pull
// `compare` off it rather than importing it by name (which fails at runtime).
import fastJsonPatch from "fast-json-patch";
const { compare } = fastJsonPatch;

import {
  ScriptOutputApiError,
  type PatchScriptOptions,
  type PublishPatchOptions,
  type ReplaceScriptOptions,
  type SaveValidationOptions,
  type ScriptSnapshot,
  type ScriptOutputStore,
} from "./script-output-store.js";

// Re-export the shared error/types so existing callers that import them from
// this module keep compiling. New code should import directly from
// ./script-output-store.js — the shared module is the single source of truth.
export {
  ScriptOutputApiError,
  type ScriptOutputApiErrorOptions,
  type PatchScriptOptions,
  type PublishPatchOptions,
  type ReplaceScriptOptions,
  type SaveValidationOptions,
  type ScriptSnapshot,
  type ScriptOutputStore,
} from "./script-output-store.js";

const SCRIPT_OUTPUT_PREFIX = "/api/anime/workbench/script-output/project-groups";
// V2 adds an isolated publish endpoint while preserving the legacy PUT/PATCH
// contracts. Same gateway base + the same
// X-Access-Key auth as V1; only the path prefix and the write shape differ.
const SCRIPT_V2_PREFIX = "/api/anime/workbench/v2/script/project-groups";
// Project-group metadata lives on a DIFFERENT controller (not under the
// script-output prefix). Same gateway base + X-Access-Key auth, same
// {code,msg,data} envelope. "有值才更新": send only the fields we want changed.
const PROJECT_GROUP_UPDATE_PATH = "/api/anime/workbench/projectGroup/updateProjectGroupInfo";
// Best-effort metadata write — keep its timeout short so a hung gateway can't
// stall init's exit for the full 30s default the script writes use.
const PROJECT_GROUP_UPDATE_TIMEOUT_MS = 5000;
const IMPORT_BODY_LIMIT_BYTES = 8 * 1024 * 1024;

export function normalizeGatewayBase(value: string): string {
  let base = value.trim().replace(/\/+$/, "");
  for (const suffix of ["/api/anime/workbench", "/api/sandbox"]) {
    if (base.endsWith(suffix)) {
      base = base.slice(0, -suffix.length);
      break;
    }
  }
  return base.replace(/\/+$/, "");
}

export function quotePath(value: string): string {
  return encodeURIComponent(String(value));
}

function apiErrorFromText(text: string, status: number, path: string): ScriptOutputApiError {
  if (status === 404 && path.includes("/imports")) {
    return new ScriptOutputApiError("backend lacks episode import API", { status, payload: text });
  }
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    const message = String(obj.msg ?? obj.message ?? `HTTP ${status}`);
    return new ScriptOutputApiError(message, { status, code: (obj.code as number | string | null) ?? null, payload: obj });
  }
  return new ScriptOutputApiError(text || `HTTP ${status}`, { status, payload: text });
}

function assertImportBodySize(label: string, body: unknown): void {
  const bytes = Buffer.byteLength(JSON.stringify(body), "utf8");
  if (bytes <= IMPORT_BODY_LIMIT_BYTES) return;
  // V2 sends the whole document (root) or an RFC6902 diff (patch) in one body —
  // neither is chunkable by episode/scene, so keep the message generic.
  throw new ScriptOutputApiError(
    `script ${label} body too large: ${bytes} bytes (limit ${IMPORT_BODY_LIMIT_BYTES})`,
  );
}

// Shared gateway transport for both V1 (RemoteScriptOutputStore) and V2
// (ScriptV2OutputStore): same X-Access-Key auth, same timeout/abort handling,
// same `{code,msg,data}` envelope unwrap and `剧本不存在` → null (allowMissing)
// convention. Extracted so the two stores can't drift on error handling.
async function gatewayRequest(
  gatewayBase: string,
  accessKey: string,
  timeoutMs: number,
  method: string,
  path: string,
  opts: { body?: unknown; allowMissing?: boolean } = {},
): Promise<unknown> {
  const rawBody = opts.body === undefined ? undefined : JSON.stringify(opts.body);
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Access-Key": accessKey,
  };
  if (rawBody !== undefined) headers["Content-Type"] = "application/json; charset=utf-8";

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let status: number;
  let text: string;
  try {
    const response = await fetch(`${gatewayBase}${path}`, {
      method,
      headers,
      body: rawBody,
      signal: controller.signal,
    });
    status = response.status;
    text = await response.text();
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw new ScriptOutputApiError("Gateway request timed out");
    }
    const reason = err instanceof Error ? err.message : String(err);
    throw new ScriptOutputApiError(`Gateway request failed: ${reason}`);
  } finally {
    clearTimeout(timer);
  }

  if (status >= 400) throw apiErrorFromText(text, status, path);

  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ScriptOutputApiError("Gateway response is not valid JSON", { status, payload: text });
    }
  }
  if (payload === null) return null;
  if (typeof payload !== "object") return payload;

  const obj = payload as Record<string, unknown>;
  const code = obj.code;
  if (code !== undefined && code !== null && String(code) !== "200") {
    const message = String(obj.msg ?? obj.message ?? "script-output API failed");
    if (opts.allowMissing && typeof message === "string" && message.includes("剧本不存在")) return null;
    throw new ScriptOutputApiError(message, { status, code: code as number | string, payload: obj });
  }
  if ("data" in obj) return obj.data;
  return obj;
}

/**
 * V2 script-output store. Same gateway base + X-Access-Key auth as V1, but:
 *   - publishPatch uses the dedicated strict-CAS publish endpoint.
 *   - replaceScript and patchScript retain the legacy endpoint contracts.
 *   - patchScript sends an RFC6902 PATCH (the compare() diff of old→edited),
 *     which V1 cannot do (it degrades to a whole-document replace).
 *
 * Selected over V1 by the `SCRIPT_OUTPUT_API_VERSION=v2` env (see
 * remoteScriptOutputStoreFromEnv). Endpoints:
 *   GET    {prefix}/{projectGroupNo}
 *   GET    {prefix}/{projectGroupNo}/revision
 *   GET    {prefix}/{projectGroupNo}/snapshot
 *   PATCH  {prefix}/{projectGroupNo}/publish   { baseRevision, patch, validation }
 *   PATCH  {prefix}/{projectGroupNo}   RFC6902 patch array (wrapped with requestId)
 *   DELETE {prefix}/{projectGroupNo}
 */
export class ScriptV2OutputStore implements ScriptOutputStore {
  readonly gatewayBase: string;
  readonly projectGroupNo: string;
  readonly accessKey: string;
  readonly timeoutMs: number;

  constructor(opts: {
    gatewayBase: string;
    projectGroupNo: string;
    accessKey: string;
    timeoutMs?: number;
  }) {
    this.gatewayBase = opts.gatewayBase;
    this.projectGroupNo = opts.projectGroupNo;
    this.accessKey = opts.accessKey;
    // checkout/publish may transfer a full large snapshot over a weak network.
    this.timeoutMs = opts.timeoutMs ?? 300_000;
  }

  static fromEnv(projectGroupNo?: string | null): ScriptV2OutputStore {
    const resolved = (projectGroupNo ?? process.env.SANDBOX_PROJECT_GROUP_NO ?? "").trim();
    if (!resolved) {
      throw new ScriptOutputApiError(
        "projectGroupNo missing; pass --project-group-no or set SANDBOX_PROJECT_GROUP_NO",
        { configMissing: "project" },
      );
    }
    const gatewayBase = normalizeGatewayBase(process.env.AWB_BASE_URL || "");
    if (!gatewayBase) {
      throw new ScriptOutputApiError("gateway URL missing; set AWB_BASE_URL", { configMissing: "gateway" });
    }
    const accessKey = (process.env.LINGJING_AWB_ACCESS_KEY || "").trim();
    if (!accessKey) {
      throw new ScriptOutputApiError("access key missing; set LINGJING_AWB_ACCESS_KEY", { configMissing: "access" });
    }
    return new ScriptV2OutputStore({ gatewayBase, projectGroupNo: resolved, accessKey });
  }

  private get projectPrefix(): string {
    return `${SCRIPT_V2_PREFIX}/${quotePath(this.projectGroupNo)}`;
  }

  async getScript(): Promise<Record<string, unknown> | null> {
    return (await this.request("GET", this.projectPrefix, { allowMissing: true })) as
      | Record<string, unknown>
      | null;
  }

  async getRevision(): Promise<Record<string, unknown> | null> {
    return (await this.request("GET", `${this.projectPrefix}/revision`, { allowMissing: true })) as
      | Record<string, unknown>
      | null;
  }

  async getSnapshot(): Promise<ScriptSnapshot | null> {
    return (await this.request("GET", `${this.projectPrefix}/snapshot`, { allowMissing: true })) as ScriptSnapshot | null;
  }

  async publishPatch(opts: PublishPatchOptions): Promise<Record<string, unknown>> {
    return (await this.request("PATCH", `${this.projectPrefix}/publish`, {
      body: {
        baseRevision: opts.baseRevision,
        patch: compare(opts.old, opts.edited),
        validation: opts.validation,
      },
    })) as Record<string, unknown>;
  }

  async replaceScript(opts: ReplaceScriptOptions): Promise<Record<string, unknown>> {
    // V2 takes the whole script in one PUT — assert the same body-size limit as
    // V1's begin body so an oversized document fails fast with the same error.
    const body = {
      requestId: opts.requestId,
      baseRevision: opts.baseRevision,
      source: opts.source ?? "ctl",
      script: opts.script,
    };
    assertImportBodySize("root", body);
    return (await this.request("PUT", this.projectPrefix, { body })) as Record<string, unknown>;
  }

  // True incremental edit: send the RFC6902 diff of old→edited. An empty diff
  // (no observable change) still goes through so the gateway can bump/no-op the
  // revision consistently with replaceScript's idempotency contract.
  async patchScript(opts: PatchScriptOptions): Promise<Record<string, unknown>> {
    const patch = compare(opts.old, opts.edited);
    const body = {
      requestId: opts.requestId,
      baseRevision: opts.baseRevision,
      source: opts.source ?? "ctl",
      patch,
    };
    // patchScript is now the primary save path — guard the diff body against the
    // same import-size limit replaceScript uses, so an oversized edit fails fast
    // with a clear error instead of an opaque gateway/Spring 413.
    assertImportBodySize("patch", body);
    return (await this.request("PATCH", this.projectPrefix, { body })) as Record<string, unknown>;
  }

  async saveValidationResult(opts: SaveValidationOptions): Promise<Record<string, unknown> | null> {
    // V2 has its own validation-result endpoint (the script lives in script_v2,
    // not the V1 tables, so the V1 path would 404 "剧本不存在"). Same body shape;
    // the V2 backend persists status/summary on the row and ignores issues.
    return (await this.request(
      "POST",
      `${SCRIPT_V2_PREFIX}/${quotePath(this.projectGroupNo)}/validation-result`,
      {
        body: {
          revision: opts.revision,
          validationStatus: opts.validationStatus,
          validationSummary: opts.validationSummary,
          issues: opts.issues,
        },
      },
    )) as Record<string, unknown> | null;
  }

  // Best-effort: set the project group's episode_count. Only episodeCount is
  // sent so the cover key is left untouched ("有值才更新"). Callers wrap this in
  // syncEpisodeCount, which swallows failures — never let it fail a script write.
  async updateEpisodeCount(episodeCount: number): Promise<void> {
    await gatewayRequest(
      this.gatewayBase,
      this.accessKey,
      Math.min(this.timeoutMs, PROJECT_GROUP_UPDATE_TIMEOUT_MS),
      "POST",
      PROJECT_GROUP_UPDATE_PATH,
      { body: { projectGroupNo: this.projectGroupNo, episodeCount } },
    );
  }

  private async request(
    method: string,
    path: string,
    opts: { body?: unknown; allowMissing?: boolean } = {},
  ): Promise<unknown> {
    return gatewayRequest(this.gatewayBase, this.accessKey, this.timeoutMs, method, path, opts);
  }
}

/**
 * Build the remote script-output store. The CLI is V2-only: every write goes to
 * the single-PUT / incremental-PATCH blob store, the same backend the frontend
 * writes to — so the two never split-brain across V1/V2. The legacy begin→commit
 * V1 client was removed; there is no version flag.
 */
export function remoteScriptOutputStoreFromEnv(projectGroupNo?: string | null): ScriptV2OutputStore {
  return ScriptV2OutputStore.fromEnv(projectGroupNo);
}
